import { Colors, ConfirmDialog, Icon } from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
    createQuestionnaireSchema,
    questionSchema,
    type QuestionnaireFormData,
} from '../../shared/QuestionnaireCreation/validation';

import type { CycleEvaluationFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationFormNavigator';
import type { QuestionActionItemsProps } from '@/pages/shared/QuestionnaireCreation/types';
import type { CycleQuestionnaire } from '@/types/cycle-evaluation/CycleQuestionnaire';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { DraggableItem, OptionProps } from '@amelio/ui-library';

import { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import { useUpdateCycleQuestionnaire } from '@/api/cycle-evaluation-services/cycle-questionnaire';
import {
    useCreateReviewQuestion,
    useDeleteReviewQuestion,
    useUpdateReviewQuestion,
} from '@/api/review-services/review-question';
import { lastSavedAtAtom, questionnaireCreationOnNextAtom } from '@/atoms/cycleEvaluation';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { CycleEvaluationSteps } from '@/constants/cycle-evaluation/CycleEvaluationSteps';
import {
    QuestionnaireFilters,
    type AdvancedOptionFilterType,
} from '@/constants/questionnaire/Filters';
import { ReviewQuestionStatus } from '@/constants/questionnaire/Question';
import { type QuestionTypeValue } from '@/constants/QuestionType';
import {
    useAmelioTranslations,
    useNavigate,
    useNavigateInNewPage,
    useRegisterPageAction,
} from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useRevieweesFromAudience } from '@/hooks/cycle-instance/useRevieweesFromAudience';
import { useToggleQuestionStatus } from '@/hooks/useToggleQuestionStatus';
import { useError } from '@/hooks/utils/useError';
import { RouteName } from '@/navigation/types';
import QuestionnaireCreation from '@/pages/shared/QuestionnaireCreation/components/QuestionnaireCreation';
import { showSuccessToast, showWarningToast } from '@/plugins/Toast';
import { getCycleEvaluationStepPath } from '@/utils/cycleEvaluation';
import { getLocalizedText } from '@/utils/localization';
import { createEmptyQuestion, filterQuestions } from '@/utils/question';
import { validationMessageKey } from '@/utils/validationMessage';

export const CycleEvaluationQuestionnairePage = () => {
    const { t } = useAmelioTranslations();
    const defaultLanguage = useSelectedAppLanguage();
    const { handleError } = useError();
    const navigate = useNavigate();
    const navigateInNewPage = useNavigateInNewPage();
    const { cycleEvaluationId } = useParams<CycleEvaluationFormNavigatorParams>();
    const setLastSavedAt = useSetAtom(lastSavedAtAtom);

    const { data: cycleEvaluation, isError, isLoading } = useGetCycleEvaluation(cycleEvaluationId);

    const reviewees = useRevieweesFromAudience({ cycleEvaluation });

    const { mutateAsync: updateQuestionnaire, isPending: isPendingUpdateQuestionnaire } =
        useUpdateCycleQuestionnaire();
    const { mutateAsync: updateQuestion, isPending: isPendingUpdateQuestion } =
        useUpdateReviewQuestion();
    const { mutateAsync: createQuestion, isPending: isPendingCreateQuestion } =
        useCreateReviewQuestion();
    const { mutateAsync: deleteQuestion, isPending: isPendingDeleteQuestion } =
        useDeleteReviewQuestion();

    const [questionToDelete, setQuestionToDelete] = useState<ReviewQuestion | undefined>(undefined);
    const [isAddingQuestion, setIsAddingQuestion] = useState<boolean>(false);
    const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
    const questionSnapshotRef = useRef<ReviewQuestion | null>(null);
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [questionTypeFilters, setQuestionTypeFilters] = useState<QuestionTypeValue[]>([]);
    const [advancedOptionsFilters, setAdvancedOptionsFilters] = useState<
        AdvancedOptionFilterType[]
    >([]);
    const [tempQuestion, setTempQuestion] = useState<ReviewQuestion>(() =>
        createEmptyQuestion(defaultLanguage),
    );

    const questionnaireSchema = useMemo(
        () => createQuestionnaireSchema(defaultLanguage),
        [defaultLanguage],
    );

    const { watch, setValue, reset } = useForm<QuestionnaireFormData>({
        resolver: zodResolver(questionnaireSchema),
        defaultValues: { id: '', title: {}, reviewQuestions: [] },
    });

    const formQuestions = watch('reviewQuestions') || [];
    const formTitle = watch('title');

    const currentQuestion = useMemo(() => {
        if (selectedQuestionId) {
            return formQuestions.find((question) => question.id === selectedQuestionId);
        }

        return tempQuestion;
    }, [selectedQuestionId, formQuestions, tempQuestion]);

    const questionList = useMemo(
        () =>
            filterQuestions({
                formQuestions,
                questionTypeFilters,
                advancedOptionsFilters,
            }),
        [formQuestions, questionTypeFilters, advancedOptionsFilters],
    );

    const handleFilterChange = (filter: string) => {
        const isSelected = selectedFilters.includes(filter);

        if (isSelected) {
            setSelectedFilters((prev) =>
                prev.filter((selectedFilter) => selectedFilter !== filter),
            );

            if (filter === QuestionnaireFilters.QUESTION_TYPE) {
                setQuestionTypeFilters([]);
            } else if (filter === QuestionnaireFilters.ADVANCED_OPTIONS) {
                setAdvancedOptionsFilters([]);
            }
        } else {
            setSelectedFilters((prev) => [...prev, filter]);
        }
    };

    const onQuestionTypeFilterChange = (filter: QuestionTypeValue) => {
        setQuestionTypeFilters((prev) => {
            if (prev.includes(filter)) {
                return prev.filter((f) => f !== filter);
            }

            return [...prev, filter];
        });
    };

    const onAdvancedOptionsFilterChange = (filter: AdvancedOptionFilterType) => {
        setAdvancedOptionsFilters((prev) => {
            if (prev.includes(filter)) {
                return prev.filter((f) => f !== filter);
            }

            return [...prev, filter];
        });
    };

    const validateQuestionnaire = useCallback(() => {
        const result = createQuestionnaireSchema(defaultLanguage).safeParse({
            id: cycleEvaluation?.cycleQuestionnaire?.id || '',
            title: formTitle,
            reviewTemplateId: cycleEvaluation?.cycleQuestionnaire?.reviewTemplateId ?? null,
            reviewQuestions: formQuestions?.length > 0 ? formQuestions : [],
        });

        if (!result.success) {
            const errorMessage = validationMessageKey(result.error.issues[0]?.message);
            showWarningToast({ message: t(errorMessage) });
            return false;
        }

        return true;
    }, [cycleEvaluation, formTitle, formQuestions, t]);

    const handleTitleActionButton = async () => {
        if (isEditingTitle) {
            if (!validateQuestionnaire()) {
                return;
            }

            try {
                const updatedQuestionnaire: CycleQuestionnaire = {
                    id: cycleEvaluation?.cycleQuestionnaire?.id || '',
                    title: formTitle,
                    cycleEvaluationId: cycleEvaluation?.id || '',
                    reviewTemplateId: cycleEvaluation?.cycleQuestionnaire?.reviewTemplateId,
                    reviewQuestions: formQuestions,
                };

                await updateQuestionnaire(updatedQuestionnaire);
                setLastSavedAt(new Date());

                onCancelEdit();
            } catch (error) {
                handleError({ error });
            }
            setIsEditingTitle(false);
        } else {
            setIsEditingTitle(true);
        }
    };

    const onTitleChange = useCallback(
        async (title: string) => {
            setValue('title', { ...formTitle, [defaultLanguage]: title });
        },
        [setValue, formTitle, defaultLanguage],
    );

    const onAddQuestionClick = useCallback(() => {
        setIsAddingQuestion(true);
        setTempQuestion(createEmptyQuestion(defaultLanguage));
    }, [defaultLanguage]);

    const onEditQuestion = useCallback(
        (question: ReviewQuestion) => {
            // First, restore any unsaved changes from the previously selected question
            if (selectedQuestionId && questionSnapshotRef.current) {
                const restoredQuestions = formQuestions.map((questionItem) =>
                    questionItem.id === selectedQuestionId
                        ? questionSnapshotRef.current!
                        : questionItem,
                );
                setValue('reviewQuestions', restoredQuestions);
            }

            // Then, set up the new question for editing
            questionSnapshotRef.current = formQuestions.find((q) => q.id === question.id) ?? null;
            setSelectedQuestionId(question.id || null);
        },
        [formQuestions, selectedQuestionId, setValue],
    );

    const onCancelEdit = useCallback(() => {
        if (selectedQuestionId && questionSnapshotRef.current) {
            const restoredQuestions = formQuestions.map((q) =>
                q.id === selectedQuestionId ? questionSnapshotRef.current! : q,
            );
            setValue('reviewQuestions', restoredQuestions);
        }
        setIsAddingQuestion(false);
        setSelectedQuestionId(null);
        setTempQuestion(createEmptyQuestion(defaultLanguage));
        questionSnapshotRef.current = null;
    }, [defaultLanguage, selectedQuestionId, formQuestions, setValue]);

    const onDeleteQuestion = useCallback((question: ReviewQuestion) => {
        if (!question.id) {
            return;
        }

        setQuestionToDelete(question);
    }, []);

    const onConfirmDeleteQuestion = useCallback(async () => {
        if (!questionToDelete?.id) {
            return;
        }

        const updatedQuestions = formQuestions.filter((q) => q.id !== questionToDelete.id);
        setValue('reviewQuestions', updatedQuestions);

        try {
            await deleteQuestion(questionToDelete.id);
            setLastSavedAt(new Date());

            showSuccessToast({
                message: t('evaluation360Questionnaire.message.questionDeleted'),
            });
        } catch (error) {
            handleError({ error });
        } finally {
            setQuestionToDelete(undefined);
        }
    }, [questionToDelete, formQuestions, deleteQuestion, setValue, handleError, setLastSavedAt, t]);

    const onSelectTemplateClick = () => {
        navigate({
            path: `${RouteName.CycleEvaluationForm}/${getCycleEvaluationStepPath(CycleEvaluationSteps.SELECT_TEMPLATE)}/${cycleEvaluationId}`,
        });
    };

    const onPreviewClick = () => {
        if (!formQuestions.some((question) => question.status === ReviewQuestionStatus.ACTIVE)) {
            showWarningToast({ message: t('evaluation360Questionnaire.message.noQuestions') });
            return;
        }

        navigateInNewPage({
            path: RouteName.SurveyAnswer,
            searchParams: {
                questionnaireId: cycleEvaluation?.cycleQuestionnaire?.id,
                revieweeId: reviewees[0]?.id,
                reviewType: cycleEvaluation?.reviewType,
                isCycleEvaluation: 'true',
            },
        });
    };

    const onReorderQuestions = useCallback(
        (items: DraggableItem<ReviewQuestion>[]) => {
            const reorderedQuestions = items.map((item, index) => ({
                ...item.data,
                sortOrder: index,
            }));

            setValue('reviewQuestions', reorderedQuestions);

            const updatedQuestionnaire: CycleQuestionnaire = {
                id: cycleEvaluation?.cycleQuestionnaire?.id || '',
                title: formTitle,
                cycleEvaluationId: cycleEvaluation?.id || '',
                reviewTemplateId: cycleEvaluation?.cycleQuestionnaire?.reviewTemplateId,
                reviewQuestions: reorderedQuestions,
            };

            updateQuestionnaire(updatedQuestionnaire).then(() => {
                setLastSavedAt(new Date());
            });
        },
        [cycleEvaluation, formTitle, setValue, updateQuestionnaire, setLastSavedAt],
    );

    const onQuestionChange = useCallback(
        (question: ReviewQuestion) => {
            if (selectedQuestionId) {
                const updatedQuestions = formQuestions.map((formQuestion) =>
                    formQuestion.id === selectedQuestionId ? question : formQuestion,
                );
                setValue('reviewQuestions', updatedQuestions);
            } else {
                setTempQuestion(question);
            }
        },
        [formQuestions, selectedQuestionId, setValue],
    );

    const onUpdateQuestion = async (question: ReviewQuestion) => {
        const result = questionSchema.safeParse(question);

        if (!result.success) {
            const errorMessage = validationMessageKey(result.error.issues[0]?.message);
            showWarningToast({ message: t(errorMessage) });
            return;
        }

        try {
            await updateQuestion(result.data);

            const updatedQuestions = formQuestions.map((existingQuestion) =>
                existingQuestion.id === result.data.id ? result.data : existingQuestion,
            );

            setValue('reviewQuestions', updatedQuestions);
            setLastSavedAt(new Date());
            onCancelEdit();

            showSuccessToast({ message: t('evaluation360Questionnaire.message.questionUpdated') });
        } catch (error) {
            handleError({ error });
        }
    };

    const onCreateQuestion = async (question: ReviewQuestion) => {
        const result = questionSchema.safeParse(question);

        if (!result.success) {
            const errorMessage = validationMessageKey(result.error.issues[0]?.message);
            showWarningToast({ message: t(errorMessage) });
            return;
        }

        try {
            const response: ReviewQuestion = await createQuestion({
                ...result.data,
                cycleQuestionnaireId: cycleEvaluation?.cycleQuestionnaire?.id,
                sortOrder: formQuestions.length,
            });

            setValue('reviewQuestions', [
                ...formQuestions,
                {
                    ...result.data,
                    id: response.id,
                },
            ]);
            setLastSavedAt(new Date());
            onCancelEdit();

            showSuccessToast({ message: t('evaluation360Questionnaire.message.questionCreated') });
        } catch (error) {
            handleError({ error });
        }
    };

    const { onToggleQuestionStatus } = useToggleQuestionStatus({
        updateQuestion,
        formQuestions,
        onSuccess: (updatedQuestions) => {
            setValue('reviewQuestions', updatedQuestions);
            setLastSavedAt(new Date());
        },
    });

    const onNext = async () => {
        if (!validateQuestionnaire()) {
            return false;
        }

        if (!formQuestions.some((question) => question.status === ReviewQuestionStatus.ACTIVE)) {
            showWarningToast({ message: t('evaluation360Questionnaire.message.noQuestions') });
            return false;
        }
        try {
            const updatedQuestionnaire: CycleQuestionnaire = {
                id: cycleEvaluation?.cycleQuestionnaire?.id || '',
                title: formTitle,
                cycleEvaluationId: cycleEvaluation?.id || '',
                reviewTemplateId: cycleEvaluation?.cycleQuestionnaire?.reviewTemplateId,
                reviewQuestions: formQuestions,
            };

            await updateQuestionnaire(updatedQuestionnaire);

            return true;
        } catch (error) {
            handleError({ error });
            return false;
        }
    };

    const filterOptions: OptionProps[] = [
        {
            label: t('evaluation360Questionnaire.questionForm.filters.advancedOptions'),
            value: QuestionnaireFilters.ADVANCED_OPTIONS,
        },
        {
            label: t('evaluation360Questionnaire.questionForm.filters.questionType'),
            value: QuestionnaireFilters.QUESTION_TYPE,
        },
    ];

    const questionActionItems: QuestionActionItemsProps[] = [
        {
            icon: <Icon name={'edit-edit-2'} />,
            label: t('common.edit'),
            onClick: (question: ReviewQuestion) => onEditQuestion(question),
        },
        {
            icon: (
                <Icon
                    name={'essential-trash'}
                    color={Colors.ERROR}
                />
            ),
            label: t('common.delete'),
            danger: true,
            onClick: (question: ReviewQuestion) => onDeleteQuestion(question),
        },
    ];

    useEffect(() => {
        if (cycleEvaluation?.cycleQuestionnaire) {
            reset({
                id: cycleEvaluation?.cycleQuestionnaire.id,
                title: cycleEvaluation?.cycleQuestionnaire.title || {},
                reviewQuestions: cycleEvaluation?.cycleQuestionnaire.reviewQuestions || [],
            });

            if (cycleEvaluation.cycleQuestionnaire.updatedAt) {
                setLastSavedAt(new Date(cycleEvaluation.cycleQuestionnaire.updatedAt));
            }
        }
    }, [cycleEvaluation, reset, setLastSavedAt]);

    useFetchErrorsHandler({
        errors: [isError],
    });

    useRegisterPageAction(questionnaireCreationOnNextAtom, onNext);

    if (isLoading) {
        return (
            <Skeleton>
                <Bone className={'mt-20 flex h-50 w-100'} />

                <Bone className={'mt-10 flex h-500 w-full'} />
            </Skeleton>
        );
    }

    return (
        <>
            <QuestionnaireCreation
                reviewees={reviewees}
                filterOptions={filterOptions}
                selectedFilters={selectedFilters}
                advancedOptionsFilters={advancedOptionsFilters}
                questionTypeFilters={questionTypeFilters}
                handleFilterChange={handleFilterChange}
                isAddingQuestion={isAddingQuestion}
                questions={questionList}
                selectedQuestionId={selectedQuestionId}
                currentQuestion={currentQuestion}
                questionActionItems={questionActionItems}
                onRowClick={onEditQuestion}
                isEditingTitle={isEditingTitle}
                title={getLocalizedText(formTitle)}
                isPendingUpdateQuestion={isPendingUpdateQuestion}
                isPendingUpdateQuestionnaire={isPendingUpdateQuestionnaire}
                isPendingCreateQuestion={isPendingCreateQuestion}
                isPendingDeleteQuestion={isPendingDeleteQuestion}
                setIsEditingTitle={handleTitleActionButton}
                onCancelTitle={() => setIsEditingTitle(false)}
                onTitleChange={onTitleChange}
                onQuestionTypeFilterChange={onQuestionTypeFilterChange}
                onAdvancedOptionsFilterChange={onAdvancedOptionsFilterChange}
                onPreviewClick={onPreviewClick}
                onAddQuestionClick={onAddQuestionClick}
                onDeleteQuestion={onDeleteQuestion}
                onCancelEdit={onCancelEdit}
                onQuestionChange={onQuestionChange}
                onReorderQuestions={onReorderQuestions}
                onUpdateQuestion={onUpdateQuestion}
                onCreateQuestion={onCreateQuestion}
                onToggleQuestionStatus={onToggleQuestionStatus}
                onSelectTemplateClick={onSelectTemplateClick}
                onTranslateClick={() =>
                    navigate({
                        path: RouteName.CycleEvaluationQuestionnaireTranslation,
                        state: {
                            cycleEvaluationId,
                            step: 'questionnaire',
                        },
                    })
                }
            />

            <ConfirmDialog
                isOpen={Boolean(questionToDelete)}
                title={t('common.attention')}
                message={t('common.deleteQuestionMessage')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={onConfirmDeleteQuestion}
                onCancel={() => setQuestionToDelete(undefined)}
                isLoading={isPendingDeleteQuestion}
                isDestructive
            />
        </>
    );
};
